import './TechEquipment.css';
import { useState, useEffect, useCallback } from 'react';
import { useParams } from "react-router-dom";
import FieldServiceAPI from '../Services/FieldService';

const STORAGE_KEY = 'techEquipmentCache';

const equipmentTypeOptions = [
    'CCTV Headend','Camera','CCTV Misc','Server','Computer','PLC','Switch','Graphics Panel','Access Control','Audio','Building Control','Duress Alarm','Perimeter','Fire Alarm','Door','Frame','Partition','Sliding Hardware','Swing Hardware','DPS','Misc Hardware','Furniture','Glass','Plumbing','Electrical','HVAC','Other'
];

const TechEquipment = () => {
    const { sessionId, customerId, locationId } = useParams();
    
    // State management
    const [equipmentDetails, setEquipmentDetails] = useState(null);
    const [equipmentList, setEquipmentList] = useState([]);
    const [originalEquipmentList, setOriginalEquipmentList] = useState([]);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    
    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [currentEquipment, setCurrentEquipment] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    
    // Filter state
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        building: '',
        floor: '',
        section: '',
        room: '',
        manufacturer: '',
        model: '',
        serialNumber: '',
        externalId: '',
        name: '',
        status: ''
    });

    // Form state for modal
    const [formData, setFormData] = useState({
        name: '',
        building: '',
        floor: '',
        section: '',
        room: '',
        notes: '',
        serialNumber: '',
        externalId: '',
        manufacturer: '',
        model: '',
        category: '',
        status: 'active',
        purchaseDate: '',
        warrantyExpiration: ''
    });

    // Load cached data from localStorage
    const loadFromCache = useCallback(() => {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (cached) {
            try {
                const data = JSON.parse(cached);
                if (data.sessionId === sessionId && data.customerId === customerId && data.locationId === locationId) {
                    setEquipmentDetails(data.equipmentDetails);
                    setEquipmentList(data.equipmentList);
                    setOriginalEquipmentList(data.originalEquipmentList);
                    return true;
                }
            } catch (err) {
                console.error('Error loading cache:', err);
            }
        }
        return false;
    }, [sessionId, customerId, locationId]);

    // Save to cache
    const saveToCache = useCallback((details, equipment, original) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                sessionId,
                customerId,
                locationId,
                equipmentDetails: details,
                equipmentList: equipment,
                originalEquipmentList: original,
                timestamp: new Date().toISOString()
            }));
        } catch (err) {
            console.error('Error saving cache:', err);
        }
    }, [sessionId, customerId, locationId]);

    // Clear cache
    const clearCache = () => {
        localStorage.removeItem(STORAGE_KEY);
    };

    // Fetch equipment details
    const fetchEquipmentDetails = async () => {
        try {
            const res = await FieldServiceAPI.getCustomerLocationEquipmentBySession(sessionId, customerId, locationId);
            if (res.status > 199 && res.status < 300) {
                console.log("Equipment details data:", res.data);
                setEquipmentDetails(res.data);
                setEquipmentList(res.data.equipment || []);
                setOriginalEquipmentList(JSON.parse(JSON.stringify(res.data.equipment || [])));
                saveToCache(res.data, res.data.equipment || [], res.data.equipment || []);
                setError("");
            } else {
                setError("No equipment details found");
            }
        } catch (err) {
            console.error(err);
            setError("This link is invalid or has expired.");
        } finally {
            setIsLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        if (sessionId && customerId && locationId) {
            const hasCached = loadFromCache();
            if (hasCached) {
                setIsLoading(false);
            } else {
                fetchEquipmentDetails();
            }
        }
    }, [sessionId, customerId, locationId, loadFromCache]);

    // Check if there are changes
    const hasChanges = useCallback(() => {
        return JSON.stringify(equipmentList) !== JSON.stringify(originalEquipmentList);
    }, [equipmentList, originalEquipmentList]);

    // Handle submit updates
    const handleSubmitUpdates = async () => {
        setIsLoading(true);
        setError("");
        setSuccess("");
        
        try {
            // Prepare equipment data with userEmail
            const equipmentDataArray = equipmentList.map(eq => ({
                ...eq,
                userEmail: 'system@cornerstoneinc.com' // You may want to get this from context/auth
            }));

            const res = await FieldServiceAPI.updateEquipmentRecords(sessionId, customerId, locationId, equipmentDataArray);
            
            if (res.status > 199 && res.status < 300) {
                setSuccess("Equipment records updated successfully.");
                setOriginalEquipmentList(JSON.parse(JSON.stringify(equipmentList)));
                clearCache();
                saveToCache(equipmentDetails, equipmentList, equipmentList);
                setTimeout(() => setSuccess(""), 5000);
            } else {
                setError("Failed to update equipment records. Please try again.");
            }
        } catch (err) {
            console.error(err);
            setError("Failed to send equipment updates. Try again when you have internet access.");
            // Keep changes in cache for retry
            saveToCache(equipmentDetails, equipmentList, originalEquipmentList);
        } finally {
            setIsLoading(false);
        }
    };

    // Open modal for new equipment
    const handleNewEquipment = () => {
        setIsEditing(false);
        setCurrentEquipment(null);
        setFormData({
            name: '',
            building: '',
            floor: '',
            section: '',
            room: '',
            notes: '',
            serialNumber: '',
            externalId: '',
            manufacturer: '',
            model: '',
            category: '',
            status: 'active',
            purchaseDate: '',
            warrantyExpiration: ''
        });
        setShowModal(true);
    };

    // Open modal for editing equipment
    const handleEditEquipment = (equipment) => {
        setIsEditing(true);
        setCurrentEquipment(equipment);
        setFormData({
            name: equipment.name || '',
            building: equipment.building || '',
            floor: equipment.floor || '',
            section: equipment.section || '',
            room: equipment.room || '',
            notes: equipment.notes || '',
            serialNumber: equipment.serialNumber || '',
            externalId: equipment.externalId || '',
            manufacturer: equipment.manufacturer || '',
            model: equipment.model || '',
            category: equipment.category || '',
            status: equipment.status || 'active',
            purchaseDate: equipment.purchaseDate ? equipment.purchaseDate.split('T')[0] : '',
            warrantyExpiration: equipment.warrantyExpiration ? equipment.warrantyExpiration.split('T')[0] : ''
        });
        setShowModal(true);
    };

    // Close modal
    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentEquipment(null);
        setFormData({
            name: '',
            building: '',
            floor: '',
            section: '',
            room: '',
            notes: '',
            serialNumber: '',
            externalId: '',
            manufacturer: '',
            model: '',
            category: '',
            status: 'active',
            purchaseDate: '',
            warrantyExpiration: ''
        });
    };

    // Handle form input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Handle hierarchical clearing for location fields
        if (name === 'building') {
            setFormData(prev => ({ ...prev, building: value, floor: '', section: '', room: '' }));
        } else if (name === 'floor') {
            setFormData(prev => ({ ...prev, floor: value, section: '', room: '' }));
        } else if (name === 'section') {
            setFormData(prev => ({ ...prev, section: value, room: '' }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // Save equipment (add or edit)
    const handleSaveEquipment = () => {
        if (!formData.name || !formData.manufacturer || !formData.model || !formData.category || !formData.building || !formData.floor || !formData.section || !formData.room) {
            alert('Please fill in required fields: Name, Manufacturer, Model, Category, Building, Floor, Section, and Room');
            return;
        }

        // Check if any location fields still have the custom placeholder
        if (formData.building === '__custom__' || formData.floor === '__custom__' || formData.section === '__custom__' || formData.room === '__custom__') {
            alert('Please enter a value for all location fields or select from existing options.');
            return;
        }

        const equipmentData = {
            ...formData,
            customer: equipmentDetails.customer.id,
            location: equipmentDetails.location.locationID,
            purchaseDate: formData.purchaseDate || null,
            warrantyExpiration: formData.warrantyExpiration || null
        };

        if (isEditing && currentEquipment) {
            // Update existing equipment
            const updatedList = equipmentList.map(eq => 
                eq.id === currentEquipment.id ? { ...eq, ...equipmentData } : eq
            );
            setEquipmentList(updatedList);
            saveToCache(equipmentDetails, updatedList, originalEquipmentList);
        } else {
            // Add new equipment (without id, backend will assign)
            const newEquipment = {
                ...equipmentData,
                createdDate: new Date().toISOString(),
                lastUpdatedDate: new Date().toISOString(),
                createdBy: 'system@cornerstoneinc.com'
            };
            const updatedList = [...equipmentList, newEquipment];
            setEquipmentList(updatedList);
            saveToCache(equipmentDetails, updatedList, originalEquipmentList);
        }

        handleCloseModal();
    };

    // Filter equipment list
    const getFilteredEquipment = () => {
        return equipmentList.filter(eq => {
            return (
                (filters.building === '' || eq.building === filters.building) &&
                (filters.floor === '' || eq.floor === filters.floor) &&
                (filters.section === '' || eq.section === filters.section) &&
                (filters.room === '' || eq.room === filters.room) &&
                (filters.manufacturer === '' || (eq.manufacturer && eq.manufacturer.toLowerCase().includes(filters.manufacturer.toLowerCase()))) &&
                (filters.model === '' || (eq.model && eq.model.toLowerCase().includes(filters.model.toLowerCase()))) &&
                (filters.serialNumber === '' || (eq.serialNumber && eq.serialNumber.toLowerCase().includes(filters.serialNumber.toLowerCase()))) &&
                (filters.externalId === '' || (eq.externalId && eq.externalId.toLowerCase().includes(filters.externalId.toLowerCase()))) &&
                (filters.name === '' || (eq.name && eq.name.toLowerCase().includes(filters.name.toLowerCase()))) &&
                (filters.status === '' || eq.status === filters.status)
            );
        });
    };

    // Get unique filter values
    const getUniqueValues = (field) => {
        const values = equipmentList.map(eq => eq[field]).filter(v => v);
        return [...new Set(values)].sort();
    };

    // Get hierarchical location values for form dropdowns
    const getAvailableFloors = () => {
        if (!formData.building || formData.building === '__custom__') return [];
        const floors = equipmentList
            .filter(eq => eq.building === formData.building)
            .map(eq => eq.floor)
            .filter(v => v);
        return [...new Set(floors)].sort();
    };

    const getAvailableSections = () => {
        if (!formData.building || !formData.floor || formData.building === '__custom__' || formData.floor === '__custom__') return [];
        const sections = equipmentList
            .filter(eq => eq.building === formData.building && eq.floor === formData.floor)
            .map(eq => eq.section)
            .filter(v => v);
        return [...new Set(sections)].sort();
    };

    const getAvailableRooms = () => {
        if (!formData.building || !formData.floor || !formData.section || formData.building === '__custom__' || formData.floor === '__custom__' || formData.section === '__custom__') return [];
        const rooms = equipmentList
            .filter(eq => eq.building === formData.building && eq.floor === formData.floor && eq.section === formData.section)
            .map(eq => eq.room)
            .filter(v => v);
        return [...new Set(rooms)].sort();
    };

    // Get hierarchical filter values
    const getFilteredFloors = () => {
        if (!filters.building) return getUniqueValues('floor');
        const floors = equipmentList
            .filter(eq => eq.building === filters.building)
            .map(eq => eq.floor)
            .filter(v => v);
        return [...new Set(floors)].sort();
    };

    const getFilteredSections = () => {
        if (!filters.building || !filters.floor) return getUniqueValues('section');
        const sections = equipmentList
            .filter(eq => eq.building === filters.building && eq.floor === filters.floor)
            .map(eq => eq.section)
            .filter(v => v);
        return [...new Set(sections)].sort();
    };

    const getFilteredRooms = () => {
        if (!filters.building || !filters.floor || !filters.section) return getUniqueValues('room');
        const rooms = equipmentList
            .filter(eq => eq.building === filters.building && eq.floor === filters.floor && eq.section === filters.section)
            .map(eq => eq.room)
            .filter(v => v);
        return [...new Set(rooms)].sort();
    };

    // Handle filter change
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        
        // Handle hierarchical clearing for filter fields
        if (name === 'building') {
            setFilters(prev => ({ ...prev, building: value, floor: '', section: '', room: '' }));
        } else if (name === 'floor') {
            setFilters(prev => ({ ...prev, floor: value, section: '', room: '' }));
        } else if (name === 'section') {
            setFilters(prev => ({ ...prev, section: value, room: '' }));
        } else {
            setFilters(prev => ({ ...prev, [name]: value }));
        }
    };

    // Clear all filters
    const handleClearFilters = () => {
        setFilters({
            building: '',
            floor: '',
            section: '',
            room: '',
            manufacturer: '',
            model: '',
            serialNumber: '',
            externalId: '',
            name: '',
            status: ''
        });
    };

    // Check if any filters are active
    const hasActiveFilters = () => {
        return filters.building || filters.floor || filters.section || filters.room || 
               filters.manufacturer || filters.model || filters.serialNumber || 
               filters.externalId || filters.name || filters.status;
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const filteredEquipment = getFilteredEquipment();

    if (isLoading) {
        return (
            <div className="tech-equipment-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading equipment data...</p>
                </div>
            </div>
        );
    }

    if (error && !equipmentDetails) {
        return (
            <div className="tech-equipment-container">
                <div className="error-container">
                    <p className="error-message">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="tech-equipment-container">
            {/* Header with customer and location info */}
            <div className="equipment-header">
                <div className="header-info">
                    <span className="customer-name">{equipmentDetails?.customer?.customerName || 'N/A'}</span>
                    <span className="separator">•</span>
                    <span className="location-name">{equipmentDetails?.location?.locationName || 'N/A'}</span>
                </div>
                <div className="header-buttons">
                    {hasChanges() && (
                        <button className="btn-submit-updates" onClick={handleSubmitUpdates} disabled={isLoading}>
                            Submit Updates
                        </button>
                    )}
                    <button className="btn-new-equipment" onClick={handleNewEquipment}>
                        New Equipment
                    </button>
                </div>
            </div>

            {/* Success/Error messages */}
            {success && (
                <div className="message-banner success-banner">
                    {success}
                </div>
            )}
            {error && (
                <div className="message-banner error-banner">
                    {error}
                </div>
            )}

            {/* Filter bar */}
            <div className="filter-section">
                <button className="filter-toggle" onClick={() => setShowFilters(!showFilters)}>
                    <span>Filters{hasActiveFilters() && <span className="filter-count"> (Active)</span>}</span>
                    <div className="filter-toggle-actions">
                        {hasActiveFilters() && !showFilters && (
                            <button 
                                className="btn-clear-filters-inline" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClearFilters();
                                }}
                            >
                                Clear
                            </button>
                        )}
                        <span className={`filter-icon ${showFilters ? 'open' : ''}`}>▼</span>
                    </div>
                </button>
                
                {showFilters && (
                    <div className="filter-content">
                        <div className="filter-grid">
                            <div className="filter-group">
                                <label>Name (Search)</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={filters.name}
                                    onChange={handleFilterChange}
                                    placeholder="Search by name..."
                                />
                            </div>

                            <div className="filter-group">
                                <label>Building</label>
                                <select name="building" value={filters.building} onChange={handleFilterChange}>
                                    <option value="">All Buildings</option>
                                    {getUniqueValues('building').map(val => (
                                        <option key={val} value={val}>{val}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-group">
                                <label>Floor</label>
                                <select name="floor" value={filters.floor} onChange={handleFilterChange} disabled={!filters.building}>
                                    <option value="">All Floors</option>
                                    {getFilteredFloors().map(val => (
                                        <option key={val} value={val}>{val}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-group">
                                <label>Section</label>
                                <select name="section" value={filters.section} onChange={handleFilterChange} disabled={!filters.building || !filters.floor}>
                                    <option value="">All Sections</option>
                                    {getFilteredSections().map(val => (
                                        <option key={val} value={val}>{val}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-group">
                                <label>Room</label>
                                <select name="room" value={filters.room} onChange={handleFilterChange} disabled={!filters.building || !filters.floor || !filters.section}>
                                    <option value="">All Rooms</option>
                                    {getFilteredRooms().map(val => (
                                        <option key={val} value={val}>{val}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-group">
                                <label>Manufacturer</label>
                                <input
                                    type="text"
                                    name="manufacturer"
                                    list="manufacturer-list"
                                    value={filters.manufacturer}
                                    onChange={handleFilterChange}
                                    placeholder="Search manufacturer..."
                                />
                                <datalist id="manufacturer-list">
                                    {getUniqueValues('manufacturer').map(val => (
                                        <option key={val} value={val} />
                                    ))}
                                </datalist>
                            </div>

                            <div className="filter-group">
                                <label>Model</label>
                                <input
                                    type="text"
                                    name="model"
                                    list="model-list"
                                    value={filters.model}
                                    onChange={handleFilterChange}
                                    placeholder="Search model..."
                                />
                                <datalist id="model-list">
                                    {getUniqueValues('model').map(val => (
                                        <option key={val} value={val} />
                                    ))}
                                </datalist>
                            </div>

                            <div className="filter-group">
                                <label>Serial Number</label>
                                <input
                                    type="text"
                                    name="serialNumber"
                                    list="serialNumber-list"
                                    value={filters.serialNumber}
                                    onChange={handleFilterChange}
                                    placeholder="Search serial number..."
                                />
                                <datalist id="serialNumber-list">
                                    {getUniqueValues('serialNumber').map(val => (
                                        <option key={val} value={val} />
                                    ))}
                                </datalist>
                            </div>

                            <div className="filter-group">
                                <label>External ID</label>
                                <input
                                    type="text"
                                    name="externalId"
                                    list="externalId-list"
                                    value={filters.externalId}
                                    onChange={handleFilterChange}
                                    placeholder="Search external ID..."
                                />
                                <datalist id="externalId-list">
                                    {getUniqueValues('externalId').map(val => (
                                        <option key={val} value={val} />
                                    ))}
                                </datalist>
                            </div>

                            <div className="filter-group">
                                <label>Status</label>
                                <select name="status" value={filters.status} onChange={handleFilterChange}>
                                    <option value="">All Statuses</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                        <button className="btn-clear-filters" onClick={handleClearFilters}>
                            Clear Filters
                        </button>
                    </div>
                )}
            </div>

            {/* Equipment list */}
            <div className="equipment-list">
                {filteredEquipment.length === 0 ? (
                    <div className="no-equipment">
                        <p>No equipment found. {equipmentList.length > 0 ? 'Try adjusting your filters.' : 'Click "New Equipment" to add one.'}</p>
                    </div>
                ) : (
                    filteredEquipment.map((equipment, index) => (
                        <div key={equipment.id || `new-${index}`} className="equipment-card" onClick={() => handleEditEquipment(equipment)}>
                            <div className="card-header">
                                <h3>{equipment.name}</h3>
                                <span className={`status-badge ${equipment.status}`}>{equipment.status}</span>
                            </div>
                            <div className="card-body">
                                <div className="info-row">
                                    <span className="label">Manufacturer:</span>
                                    <span className="value">{equipment.manufacturer || 'N/A'}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Model:</span>
                                    <span className="value">{equipment.model || 'N/A'}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Category:</span>
                                    <span className="value">{equipment.category || 'N/A'}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Location:</span>
                                    <span className="value">
                                        {[equipment.building, equipment.floor, equipment.section, equipment.room]
                                            .filter(v => v)
                                            .join(' - ') || 'N/A'}
                                    </span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Serial #:</span>
                                    <span className="value">{equipment.serialNumber || 'N/A'}</span>
                                </div>
                                {equipment.externalId && (
                                    <div className="info-row">
                                        <span className="label">External ID:</span>
                                        <span className="value">{equipment.externalId}</span>
                                    </div>
                                )}
                                <div className="info-row">
                                    <span className="label">Warranty Expires:</span>
                                    <span className="value">{formatDate(equipment.warrantyExpiration)}</span>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Equipment Modal */}
            {showModal && (
                <div className="equipment-modal" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{isEditing ? 'Edit Equipment' : 'New Equipment'}</h2>
                            <button className="close-button-white" onClick={handleCloseModal}>×</button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="form-grid">
                                <div className="form-group full-width">
                                    <label>Name *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Equipment name"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Manufacturer *</label>
                                    <input
                                        type="text"
                                        name="manufacturer"
                                        value={formData.manufacturer}
                                        onChange={handleInputChange}
                                        placeholder="Manufacturer"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Model *</label>
                                    <input
                                        type="text"
                                        name="model"
                                        value={formData.model}
                                        onChange={handleInputChange}
                                        placeholder="Model number"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Category *</label>
                                    <select name="category" value={formData.category} onChange={handleInputChange} required>
                                        <option value="">Select Category</option>
                                        {equipmentTypeOptions.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Status</label>
                                    <select name="status" value={formData.status} onChange={handleInputChange}>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Serial Number</label>
                                    <input
                                        type="text"
                                        name="serialNumber"
                                        value={formData.serialNumber}
                                        onChange={handleInputChange}
                                        placeholder="Serial number"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>External ID</label>
                                    <input
                                        type="text"
                                        name="externalId"
                                        value={formData.externalId}
                                        onChange={handleInputChange}
                                        placeholder="External ID"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Building *</label>
                                    {(formData.building === '__custom__' || (formData.building && !getUniqueValues('building').includes(formData.building))) ? (
                                        <input
                                            type="text"
                                            name="building"
                                            value={formData.building === '__custom__' ? '' : formData.building}
                                            onChange={(e) => {
                                                setFormData(prev => ({ ...prev, building: e.target.value, floor: '', section: '', room: '' }));
                                            }}
                                            onBlur={(e) => {
                                                if (!e.target.value) {
                                                    setFormData(prev => ({ ...prev, building: '', floor: '', section: '', room: '' }));
                                                }
                                            }}
                                            placeholder="Enter building name"
                                            autoFocus
                                            required
                                        />
                                    ) : (
                                        <select name="building" value={formData.building} onChange={handleInputChange} required>
                                            <option value="">Select Building</option>
                                            {getUniqueValues('building').map(val => (
                                                <option key={val} value={val}>{val}</option>
                                            ))}
                                            <option value="__custom__">+ Add New Building</option>
                                        </select>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Floor *</label>
                                    {(formData.floor === '__custom__' || (formData.floor && !getAvailableFloors().includes(formData.floor) && formData.building && formData.building !== '__custom__')) ? (
                                        <input
                                            type="text"
                                            name="floor"
                                            value={formData.floor === '__custom__' ? '' : formData.floor}
                                            onChange={(e) => {
                                                setFormData(prev => ({ ...prev, floor: e.target.value, section: '', room: '' }));
                                            }}
                                            onBlur={(e) => {
                                                if (!e.target.value) {
                                                    setFormData(prev => ({ ...prev, floor: '', section: '', room: '' }));
                                                }
                                            }}
                                            placeholder="Enter floor"
                                            autoFocus
                                            required
                                            disabled={!formData.building || formData.building === '__custom__'}
                                        />
                                    ) : (
                                        <select name="floor" value={formData.floor} onChange={handleInputChange} required disabled={!formData.building || formData.building === '__custom__'}>
                                            <option value="">Select Floor</option>
                                            {getAvailableFloors().length > 0 ? (
                                                getAvailableFloors().map(val => (
                                                    <option key={val} value={val}>{val}</option>
                                                ))
                                            ) : null}
                                            <option value="__custom__">+ Add New Floor</option>
                                        </select>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Section *</label>
                                    {(formData.section === '__custom__' || (formData.section && !getAvailableSections().includes(formData.section) && formData.floor && formData.floor !== '__custom__')) ? (
                                        <input
                                            type="text"
                                            name="section"
                                            value={formData.section === '__custom__' ? '' : formData.section}
                                            onChange={(e) => {
                                                setFormData(prev => ({ ...prev, section: e.target.value, room: '' }));
                                            }}
                                            onBlur={(e) => {
                                                if (!e.target.value) {
                                                    setFormData(prev => ({ ...prev, section: '', room: '' }));
                                                }
                                            }}
                                            placeholder="Enter section"
                                            autoFocus
                                            required
                                            disabled={!formData.building || !formData.floor || formData.building === '__custom__' || formData.floor === '__custom__'}
                                        />
                                    ) : (
                                        <select name="section" value={formData.section} onChange={handleInputChange} required disabled={!formData.building || !formData.floor || formData.building === '__custom__' || formData.floor === '__custom__'}>
                                            <option value="">Select Section</option>
                                            {getAvailableSections().length > 0 ? (
                                                getAvailableSections().map(val => (
                                                    <option key={val} value={val}>{val}</option>
                                                ))
                                            ) : null}
                                            <option value="__custom__">+ Add New Section</option>
                                        </select>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Room *</label>
                                    {(formData.room === '__custom__' || (formData.room && !getAvailableRooms().includes(formData.room) && formData.section && formData.section !== '__custom__')) ? (
                                        <input
                                            type="text"
                                            name="room"
                                            value={formData.room === '__custom__' ? '' : formData.room}
                                            onChange={(e) => {
                                                setFormData(prev => ({ ...prev, room: e.target.value }));
                                            }}
                                            onBlur={(e) => {
                                                if (!e.target.value) {
                                                    setFormData(prev => ({ ...prev, room: '' }));
                                                }
                                            }}
                                            placeholder="Enter room"
                                            autoFocus
                                            required
                                            disabled={!formData.building || !formData.floor || !formData.section || formData.building === '__custom__' || formData.floor === '__custom__' || formData.section === '__custom__'}
                                        />
                                    ) : (
                                        <select name="room" value={formData.room} onChange={handleInputChange} required disabled={!formData.building || !formData.floor || !formData.section || formData.building === '__custom__' || formData.floor === '__custom__' || formData.section === '__custom__'}>
                                            <option value="">Select Room</option>
                                            {getAvailableRooms().length > 0 ? (
                                                getAvailableRooms().map(val => (
                                                    <option key={val} value={val}>{val}</option>
                                                ))
                                            ) : null}
                                            <option value="__custom__">+ Add New Room</option>
                                        </select>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Purchase Date</label>
                                    <input
                                        type="date"
                                        name="purchaseDate"
                                        value={formData.purchaseDate}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Warranty Expiration</label>
                                    <input
                                        type="date"
                                        name="warrantyExpiration"
                                        value={formData.warrantyExpiration}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>Notes</label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        placeholder="Additional notes"
                                        rows="3"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={handleCloseModal}>
                                Cancel
                            </button>
                            <button className="btn-save" onClick={handleSaveEquipment}>
                                {isEditing ? 'Update Equipment' : 'Add Equipment'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TechEquipment;